import React from 'react'
import { Nav, NavLink, NavbarContainer, NavItems, MobileIcon, MobileMenu, MobileLink } from './NavbarStyledComponent'
import { FaBars } from 'react-icons/fa';
import SocialLinks from '../SocialLinks';
import styled from 'styled-components';

const ContactMeButton = styled.button`
  padding: 10px 20px;
  background-color: white;
  color: #000080;
  border: 2px solid #000080;
  border-radius: 5px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: #000080;
    color: white;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ThemeToggleWrapper = styled.div`
  width: 65px;
  height: 32px;
  background: ${({ darkMode }) =>
    darkMode
      ? "linear-gradient(135deg, #2d1b69, #1a0f3d)"
      : "linear-gradient(135deg, #ffd700, #ffa500)"};
  border: 2px solid ${({ darkMode }) => (darkMode ? "#854CE6" : "#ff8c00")};
  border-radius: 30px;
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0 4px;
  box-shadow: ${({ darkMode }) =>
    darkMode
      ? "0 4px 12px rgba(133, 76, 230, 0.3)"
      : "0 4px 12px rgba(255, 165, 0, 0.3)"};
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;

  &:hover {
    transform: scale(1.05);
    box-shadow: ${({ darkMode }) =>
    darkMode
      ? "0 6px 16px rgba(133, 76, 230, 0.5)"
      : "0 6px 16px rgba(255, 165, 0, 0.5)"};
  }

  &:active {
    transform: scale(0.98);
  }
`;

const SliderCircle = styled.div`
  width: 24px;
  height: 24px;
  background: ${({ darkMode }) =>
    darkMode
      ? "linear-gradient(135deg, #ffffff, #e0e0e0)"
      : "linear-gradient(135deg, #ffffff, #f5f5f5)"};
  border-radius: 50%;
  position: absolute;
  top: 50%;
  left: ${({ darkMode }) => (darkMode ? "37px" : "4px")};
  transform: translateY(-50%);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  transition: left 0.4s cubic-bezier(0.4, 0, 0.2, 1),
              transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;

  &::before {
    content: "${({ darkMode }) => (darkMode ? "🌙" : "☀️")}";
    position: absolute;
  }
`;

const ScrollProgress = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 2px;
  background: linear-gradient(90deg, #FFB800 0%, #F5D020 50%, #FFB800 100%);
  width: ${({ scroll }) => scroll}%;
  z-index: 1001;
  transition: width 0.1s linear;
`;

const ScrollProgressTrack = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: rgba(255, 255, 255, 0.05);
  z-index: 1000;
`;

const ActiveNavLink = styled(NavLink)`
  color: ${({ active, theme }) => active ? theme.primary : theme.text_primary};
  font-weight: ${({ active }) => active ? '600' : '500'};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: ${({ active }) => active ? '100%' : '0%'};
    height: 2px;
    background: ${({ theme }) => theme.primary};
    transition: width 0.3s ease;
  }
  
  &:hover::after {
    width: 100%;
  }
`;

const Navbar = ({ toggleTheme, darkMode, onContactClick, onLocationClick }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [activeSection, setActiveSection] = React.useState('');
  const [scrollProgress, setScrollProgress] = React.useState(0);

  // Track scroll progress
  React.useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const progress = (window.pageYOffset / Math.max(totalHeight, 1)) * 100;
      setScrollProgress(progress);

      // Determine active section in DOM order
      const sections = ['experience', 'projects', 'skills', 'education', 'achievement'];
      let currentSection = '';
      const scrollPos = window.scrollY + 200;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const top = element.offsetTop;
          const height = element.offsetHeight;
          if (scrollPos >= top && scrollPos < top + height) {
            currentSection = section;
            break;
          }
        }
      }

      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <ScrollProgressTrack />
      <ScrollProgress scroll={scrollProgress} />
      <Nav>
        <NavbarContainer>
          <SocialLinks onLocationClick={onLocationClick} />
          <MobileIcon>
            <FaBars onClick={() => setIsOpen(!isOpen)} />
          </MobileIcon>
          <NavItems>
            <ActiveNavLink href="#skills" active={activeSection === 'skills' ? 1 : 0}>
              Skills
            </ActiveNavLink>
            <ActiveNavLink href="#experience" active={activeSection === 'experience' ? 1 : 0}>
              Experience
            </ActiveNavLink>
            <ActiveNavLink href="#projects" active={activeSection === 'projects' ? 1 : 0}>
              Projects
            </ActiveNavLink>
            <ActiveNavLink href="#education" active={activeSection === 'education' ? 1 : 0}>
              Education
            </ActiveNavLink>
            <ActiveNavLink href="#achievement" active={activeSection === 'achievement' ? 1 : 0}>
              Achievement
            </ActiveNavLink>
            <ContactMeButton onClick={onContactClick}>Contact Me</ContactMeButton>
            <ThemeToggleWrapper onClick={toggleTheme} darkMode={darkMode}>
              <SliderCircle darkMode={darkMode} />
            </ThemeToggleWrapper>
          </NavItems>

          {isOpen && (
            <MobileMenu isOpen={isOpen}>
              <MobileLink href="#skills" onClick={() => setIsOpen(!isOpen)}>
                Skills
              </MobileLink>
              <MobileLink href="#experience" onClick={() => setIsOpen(!isOpen)}>
                Experience
              </MobileLink>
              <MobileLink href="#projects" onClick={() => setIsOpen(!isOpen)}>
                Projects
              </MobileLink>
              <MobileLink href="#education" onClick={() => setIsOpen(!isOpen)}>
                Education
              </MobileLink>
              <MobileLink href="#achievement" onClick={() => setIsOpen(!isOpen)}>
                Achievement
              </MobileLink>

              <ContactMeButton onClick={() => { onContactClick(); setIsOpen(!isOpen); }} style={{ marginTop: '10px' }}>
                Contact Me
              </ContactMeButton>
            </MobileMenu>
          )}
        </NavbarContainer>
      </Nav>
    </>
  );
};

export default Navbar;